import React, { Component } from 'react';
import API from '../../utils/API';
import LoadingWizard from '../../components/spinner/wizard';
import FieldModal from '../../components/modals/fieldModal';
import SelectField from '../../components/inputs/selectField';
import InputField from '../../components/inputs/inputField';

class SourcedVenues extends Component {
	constructor(props) {
		super(props);

		this.state = {
			searchResults: [],
			selectedVenues: {},
			loading: false,
			searching: false,
			searched: false,
			searchQuery: '',
			searchFilters: {
				location: '',
				minCapacity: '',
				eventType: '',
				style: ''
			},
			showReviewModal: false,
			reviewStep: 1, // 1 = review venues, 2 = choose/create request
			proposalRequests: [],
			selectedRequest: null,
			createNewRequest: false,
			newRequestData: {
				name: '',
				eventName: '',
				arrivalDate: '',
				departureDate: '',
				estimatedAttendees: ''
			},
			nextPageToken: null,
			loadingMore: false,
			showVenueDetail: false,
			selectedVenueDetail: null
		};
		this.scrollContainerRef = React.createRef();
	}

	async componentDidMount() {
		await this.loadProposalRequests();
	}

	loadProposalRequests = async () => {
		try {
			const res = await API().get(`Organizations/${this.props.orgId}/proposal-requests`);
			if (res.data) {
				this.setState({ proposalRequests: res.data });
			}
		} catch (error) {
			console.error('Error loading proposal requests:', error);
		}
	};

	handleSearch = async () => {
		const { searchQuery, searchFilters } = this.state;
		this.setState({ searching: true, searched: false, nextPageToken: null });

		const searchParams = {
			preferences: {
				eventType: searchFilters.eventType,
				guestCount: parseInt(searchFilters.minCapacity) || 0,
				style: searchFilters.style,
				notes: searchFilters.location
			},
			excludeIds: [],
			sessionId: Date.now().toString()
		};

		try {
			const response = await API().post(
				`Organizations/${this.props.orgId}/sourced-venues/search`,
				searchParams
			);

			if (response.data.success && response.data.venues) {
				this.setState({
					searchResults: response.data.venues,
					nextPageToken: response.data.nextPageToken || null,
					searching: false,
					searched: true
				});
			} else {
				this.setState({
					searchResults: [],
					nextPageToken: null,
					searching: false,
					searched: true
				});
			}
		} catch (error) {
			console.error('Error searching venues:', error);
			this.setState({
				searchResults: [],
				nextPageToken: null,
				searching: false,
				searched: true
			});
		}
	};

	loadMoreVenues = async () => {
		const { nextPageToken, loadingMore, searchFilters } = this.state;

		if (!nextPageToken || loadingMore) return;

		this.setState({ loadingMore: true });

		const searchParams = {
			preferences: {
				eventType: searchFilters.eventType,
				guestCount: parseInt(searchFilters.minCapacity) || 0,
				style: searchFilters.style,
				notes: searchFilters.location
			},
			excludeIds: [],
			sessionId: Date.now().toString(),
			pageToken: nextPageToken
		};

		try {
			const response = await API().post(
				`Organizations/${this.props.orgId}/sourced-venues/search`,
				searchParams
			);

			if (response.data.success && response.data.venues) {
				this.setState((prevState) => ({
					searchResults: [...prevState.searchResults, ...response.data.venues],
					nextPageToken: response.data.nextPageToken || null,
					loadingMore: false
				}));
			} else {
				this.setState({
					nextPageToken: null,
					loadingMore: false
				});
			}
		} catch (error) {
			console.error('Error loading more venues:', error);
			this.setState({
				nextPageToken: null,
				loadingMore: false
			});
		}
	};

	toggleVenueSelection = (venueId) => {
		this.setState((prevState) => {
			const selectedVenues = { ...prevState.selectedVenues };
			if (selectedVenues[venueId]) {
				delete selectedVenues[venueId];
			} else {
				const venue = prevState.searchResults.find((v) => v.id === venueId);
				selectedVenues[venueId] = venue;
			}
			return { selectedVenues };
		});
	};

	selectAllVenues = () => {
		const selectedVenues = {};
		this.state.searchResults.forEach((venue) => {
			selectedVenues[venue.id] = venue;
		});
		this.setState({ selectedVenues });
	};

	clearSelection = () => {
		this.setState({ selectedVenues: {} });
	};

	clearSearch = () => {
		this.setState({
			searchQuery: '',
			searchFilters: {
				location: '',
				minCapacity: '',
				eventType: '',
				style: ''
			},
			searchResults: [],
			selectedVenues: {},
			searched: false
		});
	};

	handleSubmitProposal = async () => {
		const { selectedVenues, createNewRequest, newRequestData, selectedRequest } = this.state;

		// Validation
		if (createNewRequest) {
			if (!newRequestData.name) {
				alert('Please enter a request name');
				return;
			}
			if (!newRequestData.eventName) {
				alert('Please enter an event name');
				return;
			}
			if (!newRequestData.arrivalDate || !newRequestData.departureDate) {
				alert('Please enter arrival and departure dates');
				return;
			}
		}
		if (!createNewRequest && !selectedRequest) {
			alert('Please select a proposal request');
			return;
		}

		this.setState({ loading: true });

		try {
			const venues = Object.values(selectedVenues);

			if (createNewRequest) {
				// Single API call to create request with venues
				const requestData = {
					name: newRequestData.name,
					eventName: newRequestData.eventName,
					arrivalDate: newRequestData.arrivalDate,
					departureDate: newRequestData.departureDate,
					estimatedAttendees: parseInt(newRequestData.estimatedAttendees) || 0,
					venues: venues.map(venue => ({
						name: venue.name,
						description: venue.description || venue.enhancedDescription || '',
						venueType: 'Hotel',
						location: venue.location || '',
						website: venue.contact?.website || venue.website || '',
						phoneNumber: venue.contact?.phone || venue.phoneNumber || '',
						contactEmail: venue.contact?.email || venue.contactEmail || '',
						primaryImage: venue.img || '',
						images: venue.additionalImages || [],
						amenities: venue.amenities || [],
						capacity: venue.capacity ? { max: venue.capacity } : {},
						source: 'search'
					}))
				};

				const res = await API().post(
					`Organizations/${this.props.orgId}/proposal-requests/with-venues`,
					requestData
				);

				// Success! Navigate to the proposal request
				window.location.href = `/${this.props.orgId}/proposal-requests/${res.data.proposalRequest._id}`;
			} else {
				// Adding to existing request - use the old flow
				const requestId = selectedRequest.value;

				// Save venues and add to request
				for (const venue of venues) {
					const venueData = {
						name: venue.name,
						description: venue.description || venue.enhancedDescription || '',
						venueType: 'Hotel',
						location: venue.location || '',
						website: venue.contact?.website || venue.website || '',
						phoneNumber: venue.contact?.phone || venue.phoneNumber || '',
						contactEmail: venue.contact?.email || venue.contactEmail || '',
						primaryImage: venue.img || '',
						images: venue.additionalImages || [],
						amenities: venue.amenities || [],
						capacity: venue.capacity ? { max: venue.capacity } : {},
						source: 'search',
						sourceDate: new Date()
					};

					const saveRes = await API().post(
						`Organizations/${this.props.orgId}/sourced-venues`,
						venueData
					);

					if (saveRes.data && saveRes.data._id) {
						await API().post(
							`Organizations/${this.props.orgId}/proposal-requests/${requestId}/venues`,
							{ venueId: saveRes.data._id }
						);
					}
				}

				// Success! Navigate to the proposal request
				window.location.href = `/${this.props.orgId}/proposal-requests/${requestId}`;
			}
		} catch (error) {
			console.error('Error submitting proposal:', error);
			alert('Error creating proposal: ' + (error.response?.data?.message || error.message));
			this.setState({ loading: false });
		}
	};

	render() {
		const {
			loading,
			searching,
			searched,
			searchResults,
			selectedVenues,
			searchFilters,
			proposalRequests
		} = this.state;

		const selectedCount = Object.keys(selectedVenues).length;
		const requestOptions = proposalRequests.map((req) => ({
			value: req._id,
			label: req.name
		}));

		const eventTypeOptions = [
			{ value: '', label: 'Any type...' },
			{ value: 'Conference', label: 'Conference' },
			{ value: 'Team Retreat', label: 'Team Retreat' },
			{ value: 'Corporate Event', label: 'Corporate Event' },
			{ value: 'Training', label: 'Training' },
			{ value: 'Meeting', label: 'Meeting' }
		];

		const styleOptions = [
			{ value: '', label: 'Any style...' },
			{ value: 'Professional', label: 'Professional' },
			{ value: 'Luxury', label: 'Luxury' },
			{ value: 'Modern', label: 'Modern' },
			{ value: 'Traditional', label: 'Traditional' },
			{ value: 'Unique', label: 'Unique' }
		];

		return (
			<div className="pageContainer" style={{ padding: 0, height: '100vh', display: 'flex', flexDirection: 'column' }}>


				{/* Content Section */}
				{!searched ? <div style={{ flex: 1, overflow: 'auto', background: '#fff' }}>{/* Hero Search Section */}
					<div style={{
						background: `radial-gradient(ellipse at 22% 38%, rgba(186, 85, 211, 0.7) 0%, transparent 60%),
                radial-gradient(ellipse at 78% 62%, rgba(255, 99, 71, 0.6) 0%, transparent 58%),
                radial-gradient(circle at 50% 50%, rgba(255, 165, 0, 0.4) 0%, transparent 70%),
                linear-gradient(135deg, #2b1055 0%, #7597de 50%, #e94057 100%)`,
						/* background: `linear-gradient(30deg, rgba(0, 255, 255, 0.3) 0%, transparent 45%),
						linear-gradient(-30deg, transparent 55%, rgba(255, 0, 255, 0.3) 100%),
						linear-gradient(90deg, rgba(138, 43, 226, 0.2) 0%, transparent 50%, rgba(0, 255, 255, 0.2) 100%),
						radial-gradient(ellipse at 28% 48%, rgba(0, 255, 255, 0.8) 0%, transparent 62%),
						radial-gradient(ellipse at 72% 52%, rgba(255, 0, 255, 0.7) 0%, transparent 58%),
						radial-gradient(circle at 50% 25%, rgba(138, 43, 226, 0.6) 0%, transparent 65%),
						linear-gradient(135deg, #120136 0%, #035aa6 50%, #40bad5 100%)` */
						padding: '100px 40px 150px 40px',
						color: 'white'
					}}>
						<div style={{ maxWidth: '1400px', margin: '0 auto' }}>
							<h1 style={{ fontSize: '2.5rem', marginBottom: '10px', fontWeight: '600' }}>
								Don't let dreams be dreams
							</h1>
							<p style={{ fontSize: '1.1rem', opacity: 0.9, marginBottom: '40px' }}>
								Find the perfect venues for your event and let us handle the rest.
							</p>

							{/* Main Search Bar */}
							<div className='flex aic' style={{
								background: 'white',
								borderRadius: '12px',
								padding: '16px 12px',
								display: 'flex',
								gap: '8px',
								flexWrap: 'wrap',
								position: 'sticky',
								top: '0',
								zIndex: '10',
								boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
							}}>
								<div className='flex aic' style={{ flex: '1 1 300px', position: 'relative' }}>
									<i className="las la-map-marker-alt" style={{
										position: 'absolute',
										left: '15px',
										top: '50%',
										transform: 'translateY(-50%)',
										fontSize: '20px',
										color: '#999'
									}} />
									<input
										type="text"
										placeholder="Location (City, State...)"
										value={searchFilters.location}
										onChange={(e) =>
											this.setState({
												searchFilters: { ...searchFilters, location: e.target.value }
											})
										}
										onKeyPress={(e) => e.key === 'Enter' && this.handleSearch()}
										style={{
											width: '100%',
											padding: '14px 14px 14px 45px',
											border: 'none',
											fontSize: '16px',
											borderRadius: '8px',
											outline: 'none'
										}}
									/>
								</div>
								<input
									type="number"
									placeholder="Min Guests"
									value={searchFilters.minCapacity}
									onChange={(e) =>
										this.setState({
											searchFilters: { ...searchFilters, minCapacity: e.target.value }
										})
									}
									onKeyPress={(e) => e.key === 'Enter' && this.handleSearch()}
									style={{
										flex: '0 1 140px',
										padding: '14px',
										border: 'none',
										fontSize: '15px',
										borderRadius: '8px',
										outline: 'none',
										background: '#f8f9fa'
									}}
								/>
								<select
									value={searchFilters.eventType}
									onChange={(e) =>
										this.setState({
											searchFilters: { ...searchFilters, eventType: e.target.value }
										})
									}
									style={{
										flex: '0 1 160px',
										padding: '14px',
										border: 'none',
										fontSize: '15px',
										borderRadius: '8px',
										outline: 'none',
										background: '#f8f9fa'
									}}
								>
									{eventTypeOptions.map(opt => (
										<option key={opt.value} value={opt.value}>{opt.label}</option>
									))}
								</select>
								<select
									value={searchFilters.style}
									onChange={(e) =>
										this.setState({
											searchFilters: { ...searchFilters, style: e.target.value }
										})
									}
									style={{
										flex: '0 1 160px',
										padding: '14px',
										border: 'none',
										fontSize: '15px',
										borderRadius: '8px',
										outline: 'none',
										background: '#f8f9fa'
									}}
								>
									{styleOptions.map(opt => (
										<option key={opt.value} value={opt.value}>{opt.label}</option>
									))}
								</select>
								<button
									onClick={this.handleSearch}
									disabled={searching}
									className='prim'
									style={{
										padding: '14px 32px',
										fontSize: '16px',
										fontWeight: '600',
										borderRadius: '8px',
										border: 'none',
										color: 'white',
										cursor: searching ? 'not-allowed' : 'pointer',
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
										opacity: searching ? 0.6 : 1
									}}
								>
									<i className="las la-search" style={{ fontSize: '18px' }} />
									{searching ? 'Searching...' : 'Search'}
								</button>
								{searched && (
									<button
										onClick={this.clearSearch}
										style={{
											fontSize: '16px',
											border: 'none',
											color: 'black'
										}}
									>
										Clear
									</button>
								)}
							</div>
						</div>
					</div>
					<div style={{ maxWidth: '1400px', margin: '0 auto' }}>

						{/* Empty State */}
						{!searching && !searched && (
							<div className="flex column aic jcc">
								<div className='flex' style={{ maxWidth: 1200, boxShadow: '0 2px 30px rgba(0,0,0,0.25)', borderRadius: '30px', background: '#fff', marginTop: '-70px', overflow: 'hidden' }}>
									<div className="p-50 flex column" style={{ maxWidth: '500px', gap: '20px' }}>
										<h5>SPOTLIGHT</h5>
										<h1>Amazing Destination and venue</h1>
										<p>Discover the perfect venue for your next event with our extensive search. Find hidden gems and popular spots tailored to your needs.</p>
										<button className="prim mt-auto " style={{ fontSize: '16px', fontWeight: '600', padding: '14px 40px' }}>
											Explore Now
										</button>
									</div>
									<div style={{ minWidth: '60%' }}>
										<img src="https://images.unsplash.com/photo-1506744038136-46273834b3fb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=60" alt="Venue Spotlight" style={{ height: '400px', minWidth: '100%', objectFit: 'cover' }} />
									</div>
								</div>
							</div>
						)}

					</div>
				</div> : <div style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
					<div className='flex column  w-100 p-20' style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 100 }}>
						{/* Main Search Bar */}
						<div className='flex jcsb'>

							<div className='flex column'>

								<div className='flex aic gap10' style={{ padding: '5px 15px', width: 800, background: '#f9f9f9', borderRadius: '20px' }}>
									<div className='flex aic' style={{ flex: '1 1 600px', position: 'relative' }}>
										<i className="las la-map-marker-alt" style={{
											position: 'absolute',
											left: '15px',
											top: '50%',
											transform: 'translateY(-50%)',
											fontSize: '20px',
											color: '#999'
										}} />
										<input
											type="text"
											placeholder="Location (City, State...)"
											value={searchFilters.location}
											onChange={(e) =>
												this.setState({
													searchFilters: { ...searchFilters, location: e.target.value }
												})
											}
											onKeyPress={(e) => e.key === 'Enter' && this.handleSearch()}
											style={{
												background: 'transparent',
												width: '100%',
												padding: '14px 14px 14px 45px',
												border: 'none',
												fontSize: '16px',
												borderRadius: '8px',
												outline: 'none'
											}}
										/>
									</div>
									<button
										onClick={this.handleSearch}
										disabled={searching}
										className='prim'
										style={{
											padding: '10px',
											fontSize: '16px',
											fontWeight: '600',
											borderRadius: '50%',
											border: 'none',
											color: 'white',
											cursor: searching ? 'not-allowed' : 'pointer',
											display: 'flex',
											alignItems: 'center',
											gap: '8px',
											opacity: searching ? 0.6 : 1
										}}
									>
										<i className="las la-search" style={{ fontSize: '18px' }} />
									</button>
								</div>
								<div className='flex aic gap20' style={{padding: '10px 20px 0px 20px'}}>
									<button style={{
										fontSize: '18px',
										border: 'none',
										color: '#2A83F1',
										background: 'transparent',
										cursor: 'pointer',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										padding: '8px',
									}}>
										<i className='las la-filter' />
									</button>
									<button className='primOutline'>
										Styles (9)
									</button>
									<button className='primOutline'>
										Event Type (12)
									</button>
								</div>
							</div>
							<div>
								<button
									className="prim"
									style={{ padding: '10px 20px', fontSize: '16px', fontWeight: '600' }}
									onClick={() => this.setState({ showReviewModal: true, reviewStep: 1 })}
								>
									<i className="las la-clipboard-list mr-10" />
									Venue List
								</button>
							</div>

						</div>
						<div className="w-100 flex aic jcsb p-20 pb-0 pt-10">
							<div className="flex column ">
								<h1>Top Venues in City</h1>
								<h2 style={{color: '#8d8d8d'}}>Displaying {searchResults.length} of XX,XXX</h2>
							</div>
							<div>
								<button className='primOutline'>
									<i className='las la-map'></i>
								</button>
							</div>
						</div>
					</div>
					<div>
						<div>
							<div className='flex'>
								{/* Search Results - Venue Cards */}
								{!searching && searchResults.length > 0 && (
									<>
										<div className="flex flex-wrap gap10 jcc pb-20">
											{searchResults.map((venue, index) => {
												const isSelected = selectedVenues[venue.id];

												return (
													<div
														key={venue.id}
														className="neuCard"
														style={{
															cursor: 'pointer',
															position: 'relative',
															border: isSelected ? '2px solid #007bff' : undefined,
															padding: 0,
															overflow: 'hidden',
															minWidth: '280px',
															maxWidth: '320px',
															flex: '1 1 300px'
														}}
													>
														{/* Selection Indicator */}
														<div
															className="flex aic jcc"
															style={{
																position: 'absolute',
																top: '10px',
																right: '10px',
																zIndex: 10,
																width: '30px',
																height: '30px',
																borderRadius: '50%',
																backgroundColor: isSelected ? '#007bff' : '#fff',
																border: '2px solid #007bff',
																cursor: 'pointer'
															}}
															onClick={(e) => {
																e.stopPropagation();
																this.toggleVenueSelection(venue.id);
															}}
														>
															{isSelected && <i className="las la-check" style={{ color: '#fff' }} />}
														</div>

														{/* Venue Image - native browser loading */}
														{venue.img && (
															<img
																src={venue.img}
																alt={venue.name}
																loading="lazy"
																style={{ width: '100%', height: '180px', objectFit: 'cover' }}
															/>
														)}

														{/* Venue Details */}
														<div className="p-10">
															<h3 className="m-0">{venue.name}</h3>

															<div className="mt-10">
																{venue.review > 0 && (
																	<div className="flex aic mb-5">
																		<i className="las la-star mr-5" style={{ color: '#f5a623' }} />
																		<span className="mr-5">{venue.review.toFixed(1)}</span>
																		{venue.totalReviews > 0 && (
																			<span className="small grey">({venue.totalReviews})</span>
																		)}
																	</div>
																)}

																{venue.location && (
																	<p className="grey mb-0 small">
																		<i className="las la-map-marker-alt mr-5" />
																		{venue.location}
																	</p>
																)}

																{venue.capacity && (
																	<p className="grey mb-0 small mt-5">
																		<i className="las la-users mr-5" />
																		Up to {venue.capacity} guests
																	</p>
																)}
															</div>
														</div>
													</div>
												);
											})}
										</div>

										{/* Load More Button */}
										{this.state.nextPageToken && (
											<div className="flex jcc pb-40">
												<button
													className="prim"
													onClick={this.loadMoreVenues}
													disabled={this.state.loadingMore}
													style={{
														padding: '12px 32px',
														fontSize: '16px',
														fontWeight: '600',
														opacity: this.state.loadingMore ? 0.6 : 1
													}}
												>
													{this.state.loadingMore ? (
														<>
															<span className="spinner-border spinner-border-sm mr-10" role="status" />
															Loading More...
														</>
													) : (
														<>
															<i className="las la-plus-circle mr-10" />
															Load More Venues
														</>
													)}
												</button>
											</div>
										)}
									</>
								)}
							</div>
						</div>
					</div>
				</div>}

				{/* Review Venues Wizard Modal */}
				{this.state.showReviewModal && (
					<FieldModal
						isOpen={this.state.showReviewModal}
						toggle={() => this.setState({
							showReviewModal: false,
							reviewStep: 1,
							createNewRequest: false,
							selectedRequest: null,
							newRequestName: ''
						})}
						size="medium"
						modalTitle={this.state.reviewStep === 1 ? 'Review Selected Venues' : 'Create Proposal Request'}
						actionButtonLabel={this.state.reviewStep === 1 ? 'Next' : (this.state.createNewRequest ? 'Create Request' : 'Add to Request')}
						actionButton={this.state.reviewStep === 1 ? () => this.setState({ reviewStep: 2 }) : this.handleSubmitProposal}
						cancelButtonLabel={this.state.reviewStep === 2 ? 'Back' : 'Cancel'}
						cancelOnClick={this.state.reviewStep === 2 ? () => this.setState({ reviewStep: 1 }) : undefined}
						bodyContent={() => {
							if (this.state.reviewStep === 1) {
								// Step 1: Review Venues
								return (
									<div>
										<p className="mb-15">Review and confirm your selected venues before proceeding.</p>
										<div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '15px' }}>
											{Object.values(selectedVenues).map((venue) => (
												<div key={venue.id} style={{
													display: 'flex',
													alignItems: 'center',
													gap: '15px',
													padding: '15px',
													marginBottom: '10px',
													border: '1px solid #e0e0e0',
													borderRadius: '8px',
													background: '#fff'
												}}>
													{venue.img && (
														<img
															src={venue.img}
															alt={venue.name}
															style={{
																width: '80px',
																height: '80px',
																objectFit: 'cover',
																borderRadius: '8px',
																flexShrink: 0
															}}
														/>
													)}
													<div style={{ flex: 1, minWidth: 0 }}>
														<h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{venue.name}</h4>
														{venue.location && (
															<p style={{ color: '#666', fontSize: '14px', margin: '5px 0 0 0' }}>
																<i className="las la-map-marker-alt" style={{ marginRight: '5px' }} />
																{venue.location}
															</p>
														)}
														{venue.capacity && (
															<p style={{ color: '#666', fontSize: '14px', margin: '5px 0 0 0' }}>
																<i className="las la-users" style={{ marginRight: '5px' }} />
																Up to {venue.capacity} guests
															</p>
														)}
													</div>
													<button
														className="neu"
														onClick={() => this.toggleVenueSelection(venue.id)}
														style={{ flexShrink: 0 }}
													>
														<i className="las la-times" />
													</button>
												</div>
											))}
										</div>
									</div>
								);
							} else {
								// Step 2: Choose/Create Request
								return (
									<>
										<h3 className="mb-20">Choose Proposal Request Option</h3>

										<div className="mb-20">
											<label className="flex aic mb-15" style={{ cursor: 'pointer' }}>
												<input
													type="radio"
													name="requestOption"
													checked={!this.state.createNewRequest}
													onChange={() => this.setState({ createNewRequest: false })}
													style={{ marginRight: '10px' }}
												/>
												<span>Add to existing proposal request</span>
											</label>

											{!this.state.createNewRequest && (
												<div className="ml-30">
													<SelectField
														label="Select Proposal Request"
														value={this.state.selectedRequest}
														options={requestOptions}
														onChange={(option) => this.setState({ selectedRequest: option })}
														placeholder="Choose a proposal request..."
													/>
												</div>
											)}
										</div>

										<div>
											<label className="flex aic mb-15" style={{ cursor: 'pointer' }}>
												<input
													type="radio"
													name="requestOption"
													checked={this.state.createNewRequest}
													onChange={() => this.setState({ createNewRequest: true })}
													style={{ marginRight: '10px' }}
												/>
												<span>Create new proposal request</span>
											</label>

											{this.state.createNewRequest && (
												<div className="ml-30">
													<InputField
														label="Request Name *"
														value={this.state.newRequestData.name}
														onChange={(e) => this.setState({
															newRequestData: { ...this.state.newRequestData, name: e.target.value }
														})}
														placeholder="e.g., Summer Conference 2026"
													/>
													<InputField
														label="Event Name *"
														value={this.state.newRequestData.eventName}
														onChange={(e) => this.setState({
															newRequestData: { ...this.state.newRequestData, eventName: e.target.value }
														})}
														placeholder="e.g., Annual Sales Meeting"
													/>
													<div className="flex gap10">
														<InputField
															label="Arrival Date *"
															type="date"
															value={this.state.newRequestData.arrivalDate}
															onChange={(e) => this.setState({
																newRequestData: { ...this.state.newRequestData, arrivalDate: e.target.value }
															})}
														/>
														<InputField
															label="Departure Date *"
															type="date"
															value={this.state.newRequestData.departureDate}
															onChange={(e) => this.setState({
																newRequestData: { ...this.state.newRequestData, departureDate: e.target.value }
															})}
														/>
													</div>
													<InputField
														label="Estimated Attendees"
														type="number"
														value={this.state.newRequestData.estimatedAttendees}
														onChange={(e) => this.setState({
															newRequestData: { ...this.state.newRequestData, estimatedAttendees: e.target.value }
														})}
														placeholder="e.g., 150"
													/>
												</div>
											)}
										</div>
									</>
								);
							}
						}}
					/>
				)}

				<LoadingWizard text="Processing..." loading={loading} />
			</div>
		);
	}
}

export default SourcedVenues;
